import { Component, type ErrorInfo, type ReactNode } from 'react';
import { StaleClientError } from '@immo/api-contract';
import { DEFAULT_LOCALE, dictionaries } from '@immo/i18n';
import { captureFrontendError } from '@/lib/errors/error-context';

type Props = { children: ReactNode };
type State = { hasError: boolean; isOutdated: boolean };

/**
 * The boundary sits outside `<I18nProvider>` (see below), so the copy is read
 * off the dictionary object rather than through `useTranslations`. The locale is
 * the default rather than the operator's choice: the preference lives behind the
 * provider this fallback cannot assume is alive.
 */
const messages = dictionaries[DEFAULT_LOCALE];

/**
 * Top-level error boundary wrapping `<RouterProvider>` so a render error
 * inside the router or a context provider does not blow the entire tab.
 * Per ADR 0014 this is the SPA equivalent of the App Router
 * `global-error.tsx` floor — see the 2026-05-08 amendment translation table.
 *
 * Routes have their own `errorElement` for ordinary loader / element
 * failures; this boundary only catches the residual render-tree errors.
 * The fallback is intentionally chrome-free (no `<I18nProvider>`, no
 * shadcn primitives) because this boundary may itself sit outside those
 * providers when they are the thing that failed.
 *
 * It renders two states. `StaleClientError` — this bundle read a response from
 * a release it was not built against — is fixable by reloading, and says so.
 * Everything else keeps the generic state: an error we cannot attribute must
 * never be presented as "your tab is old", which would send the operator
 * reloading forever while the real defect stays hidden.
 */
export class RootErrorBoundary extends Component<Props, State> {
  override state: State = { hasError: false, isOutdated: false };

  static getDerivedStateFromError(error: unknown): State {
    return { hasError: true, isOutdated: error instanceof StaleClientError };
  }

  override componentDidCatch(error: Error, info: ErrorInfo): void {
    captureFrontendError(error, 'root-error-boundary', { componentStack: info.componentStack });
  }

  override render(): ReactNode {
    if (!this.state.hasError) return this.props.children;

    if (this.state.isOutdated) {
      return (
        <main className="mx-auto flex min-h-screen max-w-3xl flex-col items-center justify-center gap-4 bg-canvas p-6 font-sans text-text-primary">
          <h1 className="text-3xl font-semibold tracking-tight">{messages.common['outdated-view'].title}</h1>
          <p className="text-text-tertiary">{messages.errors.app.outdated}</p>
          <button
            type="button"
            className="bg-interactive text-interactive-on hover:bg-interactive-hover rounded-md px-4 py-2 font-medium"
            onClick={() => window.location.reload()}
          >
            {messages.common['outdated-view'].action}
          </button>
        </main>
      );
    }

    return (
      <main className="mx-auto flex min-h-screen max-w-3xl flex-col items-center justify-center gap-4 bg-canvas p-6 font-sans text-text-primary">
        <h1 className="text-3xl font-semibold tracking-tight">Something went wrong.</h1>
        <p className="text-text-tertiary">Please reload the page.</p>
      </main>
    );
  }
}
